<template>
  <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div class="flex items-center gap-2 text-sm font-semibold text-gray-800">
        <MapPin class="h-4 w-4 text-teal-600" />
        <span>Contractor map</span>
      </div>
      <slot name="actions" />
    </div>

    <div class="relative">
      <div :class="['w-full', mapHeightClass]" ref="mapContainer"></div>
      <div v-if="isLoading" class="absolute inset-0 bg-white/80 flex items-center justify-center">
        <div class="flex items-center gap-3 text-sm text-gray-600">
          <div class="h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading map...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { MapPin } from 'lucide-vue-next';
import { useGoogleMaps } from '@/composables/useGoogleMaps';

const props = defineProps({
  contractors: { type: Array, default: () => [] },
  selectedId: { type: [String, Number], default: null },
  mapHeightClass: { type: String, default: 'h-[420px]' },
  poolLocation: { type: Object, default: null },
});

const emit = defineEmits(['marker-click']);

const mapContainer = ref(null);
const isLoading = ref(true);
let map = null;
let markers = new Map();
let infoWindow = null;
let bounds = null;
let poolMarker = null;
let stylesInjected = false;

const { loadScript } = useGoogleMaps();
const center = { lat: -33.8688, lng: 151.2093 };

const mapStyles = [
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#484848' }],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [{ color: '#f7f7f7' }],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      { saturation: -100 },
      { lightness: 45 },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'simplified' }],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      { color: '#0ea5e9' },
      { visibility: 'on' },
      { lightness: 70 },
    ],
  },
];

const getContractorId = (contractor) =>
  contractor?._computedId ??
  contractor?.id ??
  contractor?.contractorId ??
  contractor?.email ??
  contractor?.full_name;

const getMarkerIcon = (active = false) => ({
  path: 'M12 2C7.9 2 4.5 5.4 4.5 9.5c0 5.2 7.5 11.5 7.5 11.5s7.5-6.3 7.5-11.5C19.5 5.4 16.1 2 12 2Z',
  fillOpacity: 1,
  fillColor: active ? '#0ea5e9' : '#0284c7', // Uniform blue for contractors
  scale: active ? 1.7 : 1.45,
  strokeColor: active ? '#0ea5e9' : '#0369a1',
  strokeWeight: 1.6,
  anchor: new google.maps.Point(12, 24),
});

const getPoolMarkerIcon = () => ({
  path: 'M12 2C8.1 2 5 5.1 5 9c0 5.4 7 13 7 13s7-7.6 7-13c0-3.9-3.1-7-7-7Zm0 9.8a2.8 2.8 0 1 1 0-5.6 2.8 2.8 0 0 1 0 5.6Z',
  fillOpacity: 1,
  fillColor: '#f97316', // Orange for pool/company
  scale: 1.75,
  strokeColor: '#ea580c',
  strokeWeight: 1.8,
  anchor: new google.maps.Point(12, 26),
});

const buildInfoWindowContent = (contractor) => {
  const safeName = contractor?.full_name || 'Contractor';
  const role = contractor?.current_role || '';
  const photo = contractor?.photoUrl || contractor?.photo;
  const availability = contractor?.availability || '';
  const location = contractor?.location || 'Sydney';
  const initials = safeName
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('') || 'C';

  const imgTag = photo
    ? `<div style="width:56px;height:56px;overflow:hidden;border-radius:9999px;border:1px solid #e2e8f0;margin-right:12px"><img src="${photo}" alt="${safeName}" style="width:100%;height:100%;object-fit:cover;" /></div>`
    : `<div style="width:56px;height:56px;border-radius:9999px;background:#dbeafe;margin-right:12px;display:flex;align-items:center;justify-content:center;font-size:14px;color:#1d4ed8;font-weight:700;border:1px solid #bfdbfe;">${initials}</div>`;

  return `<div style="min-width:240px;display:flex;align-items:center;font-family:Inter, system-ui, -apple-system, sans-serif;">
      ${imgTag}
      <div>
        <div style="font-weight:700;font-size:14px;color:#0f172a;line-height:1.2;">${safeName}</div>
        ${role ? `<div style="font-size:12px;color:#475569;margin-top:2px;">${role}</div>` : ''}
        <div style="display:flex;gap:8px;margin-top:6px;font-size:11px;color:#0f766e;font-weight:600;">
          <span>📍 ${location}</span>
          ${availability ? `<span>• ${availability}</span>` : ''}
        </div>
      </div>
    </div>`;
};

const openInfoWindow = (contractor, marker) => {
  if (!infoWindow || !marker) return;
  infoWindow.setContent(buildInfoWindowContent(contractor));
  infoWindow.open({
    map,
    anchor: marker,
    shouldFocus: false,
  });
};

const injectInfoWindowStyles = () => {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'tp-map-iw-style';
  style.textContent = `
    .gm-style .gm-ui-hover-effect {
      display: none !important; /* Hide default close button */
    }
    .gm-style .gm-style-iw-c {
      padding: 4px 8px 8px 8px !important;
      border-radius: 12px !important;
    }
    .gm-style .gm-style-iw-d {
      overflow: visible !important;
    }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
};

const hasPoolLocation = () =>
  props.poolLocation &&
  Number.isFinite(props.poolLocation.lat) &&
  Number.isFinite(props.poolLocation.lng);

const updateMarkers = () => {
  if (!map || typeof google === 'undefined') return;
  const seen = new Set();
  bounds = new google.maps.LatLngBounds();

  props.contractors.forEach((contractor) => {
    const id = getContractorId(contractor);
    const position = contractor?.coordinates;
    if (!id || !position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return;

    seen.add(id);
    let marker = markers.get(id);
    if (!marker) {
      marker = new google.maps.Marker({
        position,
        map,
        icon: getMarkerIcon(id === props.selectedId),
        optimized: true,
      });
      marker.addListener('click', () => {
        emit('marker-click', contractor);
        openInfoWindow(contractor, marker);
      });
      markers.set(id, marker);
    } else {
      marker.setPosition(position);
    }
    marker.setIcon(getMarkerIcon(id === props.selectedId));
    bounds.extend(position);
  });

  markers.forEach((marker, id) => {
    if (!seen.has(id)) {
      marker.setMap(null);
      markers.delete(id);
    }
  });

  if (poolMarker) {
    poolMarker.setMap(null);
    poolMarker = null;
  }

  if (hasPoolLocation()) {
    poolMarker = new google.maps.Marker({
      position: props.poolLocation,
      map,
      icon: getPoolMarkerIcon(),
      zIndex: 200,
    });
    poolMarker.addListener('click', () => {
      const poolName = (props.poolLocation && props.poolLocation.title) || 'Talent pool';
      infoWindow?.setContent(`<div style="min-width:220px;display:flex;align-items:center;gap:10px;font-weight:700;color:#0f172a;font-size:14px;font-family:Inter, system-ui, -apple-system, sans-serif;"><span style="display:inline-flex;align-items:center;justify-content:center;height:36px;width:36px;border-radius:9999px;background:#fff7ed;border:1px solid #fed7aa;color:#c2410c;font-size:16px;">🏢</span><div><div>${poolName}</div></div></div>`);
      infoWindow?.open({ map, anchor: poolMarker, shouldFocus: false });
    });
    bounds.extend(props.poolLocation);
  }

  const hasAnyBounds = typeof bounds.isEmpty === 'function' ? !bounds.isEmpty() : seen.size > 0 || hasPoolLocation();

  if (hasAnyBounds) {
    map.fitBounds(bounds);
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom();
      if (zoom > 14) {
        map.setZoom(14);
      }
    });
  } else {
    map.setCenter(center);
    map.setZoom(11);
  }
};

const highlightSelected = () => {
  if (!map) return;
  const activeContractor = props.contractors.find(
    (c) => getContractorId(c) === props.selectedId
  );

  markers.forEach((marker, id) => {
    marker.setIcon(getMarkerIcon(id === props.selectedId));
    if (id === props.selectedId && activeContractor) {
      openInfoWindow(activeContractor, marker);
    }
  });

  if (!props.selectedId && infoWindow) {
    infoWindow.close();
  }
};

const initMap = async () => {
  try {
    await loadScript();
    if (!mapContainer.value) return;

    map = new google.maps.Map(mapContainer.value, {
      zoom: 12,
      center,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      styles: mapStyles,
    });

    infoWindow = new google.maps.InfoWindow({ maxWidth: 320 });
    injectInfoWindowStyles();
    bounds = new google.maps.LatLngBounds();
    isLoading.value = false;
    updateMarkers();
  } catch (error) {
    console.error(error);
    isLoading.value = false;
  }
};

onMounted(() => {
  initMap();
});

watch(
  () => props.contractors,
  () => {
    updateMarkers();
  },
  { deep: true }
);

watch(
  () => props.selectedId,
  () => {
    highlightSelected();
  }
);

watch(
  () => props.poolLocation,
  () => {
    updateMarkers();
  },
  { deep: true }
);

onBeforeUnmount(() => {
  markers.forEach((marker) => marker.setMap(null));
  markers.clear();
  if (infoWindow) {
    infoWindow.close();
  }
});
</script>
